// UnitClassifyStep.js
//
// The step of the unit view for one classified column (Spec 2 sections 4.4 and 6.4): every
// distinct value the column holds in this table, and the class Decode put it in — the class
// one of whose aliases the value most resembles, or Unclassified when none is close enough.
// Unclassified values are listed first, for the person to place. Their choices are a draft,
// applied in one Classify edit; when the table stops being editable before they are
// applied, the draft is set aside until the person restores or discards it
// (UnitStepFrame's useStepDraft).
//
// Each decision is keyed by the value, and refers to the source column the classified
// column is mapped from (refers_to = { column }): a choice made on one source does not
// carry over to another, so every choice the person makes names the source as it stands.
//
// The record's decision list is inline while it fits. A long one is stored as a blob under
// the unit's own process_summaries folder, named by the record in place of its decisions,
// and is read from there (recordWithListLoaded, which follows no path outside that folder);
// a response for any other path is ignored.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import UnitStepFrame, { ApplyBar, SetAsideNotice, targetLabel, useStepDraft } from './UnitStepFrame';
import {
  activeDecisions, activeValue, decisionListOf, spilledListPath, withDecisionList,
} from '../../../utils/decisions';
import { editUnitDecisions, recordWithListLoaded, requestUnit } from '../../../services/cleaningGestures';

export const UNCLASSIFIED = 'Unclassified';
const PAGE_ROWS = 50;
const MAP = 'map';

// Props: number, companyId, tableId, unit, column (the classified column's name),
// configuration (table.classification[column]: { classes: { class: [aliases] }, threshold }),
// verdicts, editBlock, fileDeletionSet (the file's deletion is set: Retry and the pager are
// disabled, Spec 5 section 5), and the unit view's draftEpoch and runUnitChange(send, onApplied).
export default function UnitClassifyStep({
  number,
  companyId,
  tableId,
  unit,
  column,
  configuration,
  verdicts,
  editBlock,
  fileDeletionSet,
  draftEpoch,
  runUnitChange,
}) {
  const processId = `classify:${column}`;
  const record = unit.processes?.[processId] || null;
  const spilledPath = record ? spilledListPath(record) : null;

  const [spilled, setSpilled] = useState(null);        // { unitId, path, decisions }
  const [loadState, setLoadState] = useState('idle');  // 'idle' | 'loading' | 'failed'
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { changes: draft, setAsideChanges, change, clear, restore } = useStepDraft(draftEpoch, {});  // { [value]: class }
  const [page, setPage] = useState(0);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setSpilled(null);
    if (!spilledPath || !record) {
      setLoadState('idle');
      return undefined;
    }
    let cancelled = false;
    setLoadState('loading');
    recordWithListLoaded(companyId, tableId, unit.id, record)
      .then((loaded) => {
        if (cancelled) return;
        setSpilled({ unitId: unit.id, path: spilledPath, decisions: decisionListOf(loaded) });
        setLoadState('idle');
      })
      .catch((error) => {
        console.error('Failed to load a spilled classification:', error);
        if (!cancelled) setLoadState('failed');
      });
    return () => { cancelled = true; };
    // Only the list is kept, and a list stored anew has a new path: the effect follows the
    // path, not every snapshot of the record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, tableId, unit.id, spilledPath, loadAttempt]);

  // The live record with its decisions in hand, whichever way it stores them; null until a
  // spilled list for the record's current path has arrived.
  let readable = null;
  if (record && !spilledPath) readable = record;
  if (record && spilledPath && spilled && spilled.unitId === unit.id && spilled.path === spilledPath) {
    readable = withDecisionList(record, spilled.decisions);
  }

  const decided = readable ? activeDecisions(readable) : [];
  const rows = [
    ...decided.filter(({ version }) => version.value === UNCLASSIFIED),
    ...decided.filter(({ version }) => version.value !== UNCLASSIFIED),
  ];
  const currentClass = new Map(decided.map(({ key, version }) => [String(key), version.value]));
  const classes = [...Object.keys(configuration?.classes || {}), UNCLASSIFIED];
  const unclassifiedCount = rows.filter(({ version }) => version.value === UNCLASSIFIED).length;

  // A draft as it stands against the decisions now: a value no longer in the column, or
  // already in the class chosen, is no change.
  const changesIn = (choices) => Object.entries(choices).filter(([value, chosen]) => (
    currentClass.has(value) && currentClass.get(value) !== chosen
  ));
  const changes = changesIn(draft);
  const setAsideCount = setAsideChanges === null ? 0 : changesIn(setAsideChanges).length;
  const choicesDisabled = Boolean(editBlock) || applying || setAsideCount > 0;

  const choose = (value, chosen) => {
    change((previous) => {
      const next = { ...previous };
      if (currentClass.get(value) === chosen) delete next[value];
      else next[value] = chosen;
      return next;
    });
  };

  const apply = async () => {
    // The source the column is mapped from, as the Map step has it now.
    const source = unit.processes?.[MAP] ? activeValue(unit.processes[MAP], column) : null;
    const entries = changes.map(([value, chosen]) => ({
      key: value, value: chosen, refersTo: { column: source },
    }));
    setApplying(true);
    await runUnitChange(() => editUnitDecisions(companyId, tableId, unit.id, processId, entries), clear);
    setApplying(false);
  };

  // The frame counts the person's changes from the decisions, so it is handed the record
  // with its decisions in hand; a spilled list counts once it has arrived.
  let frameRecord = readable;
  if (!frameRecord && record) frameRecord = withDecisionList(record, []);

  const columnLabel = targetLabel(column);
  const lastPage = Math.max(Math.ceil(rows.length / PAGE_ROWS) - 1, 0);
  const shownPage = Math.min(page, lastPage);
  const shown = rows.slice(shownPage * PAGE_ROWS, (shownPage + 1) * PAGE_ROWS);

  let body;
  if (!record) {
    body = <Typography variant="body2" color="text.secondary">Not run on this table yet.</Typography>;
  } else if (loadState === 'loading') {
    body = <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  } else if (loadState === 'failed') {
    body = (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" size="small" disabled={fileDeletionSet} onClick={() => setLoadAttempt((n) => n + 1)}>
            Retry
          </Button>
        )}
      >
        {`The ${columnLabel} classes could not be loaded.`}
      </Alert>
    );
  } else if (rows.length === 0) {
    body = <Typography variant="body2" color="text.secondary">{`No ${columnLabel} values in this table.`}</Typography>;
  } else {
    body = (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>In the file</TableCell>
                <TableCell>Class</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(({ key, version }) => {
                const value = String(key);
                const changed = changes.some(([changedValue]) => changedValue === value);
                return (
                  <TableRow key={value}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{value}</Typography></TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={changed ? draft[value] : version.value}
                        disabled={choicesDisabled}
                        onChange={(event) => choose(value, event.target.value)}
                        SelectDisplayProps={{ 'aria-label': `Class of ${value}` }}
                        sx={{ minWidth: 160 }}
                      >
                        {classes.map((className) => (
                          <MenuItem key={className} value={className}>{className}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      {changed && <Chip size="small" color="info" variant="outlined" label="changed" />}
                      {!changed && version.made_by === 'person' && <Chip size="small" variant="outlined" label="You" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_ROWS && (
          <TablePagination
            component="div"
            count={rows.length}
            page={shownPage}
            rowsPerPage={PAGE_ROWS}
            rowsPerPageOptions={[]}
            disabled={fileDeletionSet}
            onPageChange={(event, next) => setPage(next)}
          />
        )}
      </>
    );
  }

  return (
    <UnitStepFrame
      number={number}
      title={columnLabel}
      verdicts={verdicts}
      record={frameRecord}
      menu
      editBlock={editBlock}
      onRequest={(kind) => runUnitChange(() => requestUnit(companyId, tableId, unit.id, processId, kind))}
      defaultOpen={unclassifiedCount > 0 || verdicts.length > 0}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {unclassifiedCount > 0
          ? `Each value in the file, and the class Decode put it in. ${unclassifiedCount} matched no class; place them first.`
          : 'Each value in the file, and the class Decode put it in.'}
      </Typography>
      <SetAsideNotice changeCount={setAsideCount} editBlock={editBlock} onRestore={restore} onDiscard={clear} />
      {body}
      <ApplyBar
        changeCount={changes.length}
        editBlock={editBlock}
        applying={applying}
        onApply={apply}
        onDiscard={clear}
      />
    </UnitStepFrame>
  );
}
